import { canUseStandaloneTask } from '../../../../components/chartActions/credentialCapabilities';
import { CAPABILITIES, rowLacksCapability, selectionLacksCapability } from '../../../../util/inboxRowCapabilities';

// Shared gating/derivation for the referral & inbox bulk actions. Consumed by
// both ReferralActionBar (the top toolbar) and RowQuickActions (the per-row
// hover menu) so the two can never drift. Pass a single-element selection
// (`[record]`) to evaluate one row's action availability exactly as the toolbar
// would with that record selected.

// Mirrors Api::V1::AssignedDocumentsController::MAX_BULK_ASSIGN_RECORDS, so an
// over-sized selection grays the action out instead of earning a 422.
export const MAX_BULK_ASSIGN_RECORDS = 50;

// AssignedDocument::ASSIGNABLE_PARENT_TYPES. A receiver_type absent here (a
// jotform submission) has no assignable parent.
const ASSIGNABLE_PARENT_TYPES = {
  received_fax: 'ReceivedFax',
  receiver: 'Receiver',
  email_attachment: 'EmailAttachment',
  reply: 'Reply',
};

// Resolves a selection to the bulk-assign payload and to whether the action may be
// offered at all (ENG-3409). Both come from here so the toolbar can never enable
// an action whose payload would then silently drop rows: one unassignable row
// makes the whole selection ineligible. A row that denies `can_assign` has no assignable parent.
export const deriveAssignmentTargets = ({
  selectedData = [],
  allDataOnPage = [],
  selectedAll = selectedData[0] === 'all',
}) => {
  const rows = selectedAll
    ? allDataOnPage
    : selectedData.filter((record) => record && record !== 'all');
  const records = rows.map((record) => ({
    send_token: record.send_token,
    parent_type: rowLacksCapability(record, CAPABILITIES.ASSIGN) ? undefined : ASSIGNABLE_PARENT_TYPES[record.receiver_type],
    // send_token is not unique -- an archived twin can share one -- so the row's own
    // record identity travels with it and breaks the tie server-side. It only ever
    // breaks ties, so its absence must not make the selection unassignable.
    record_id: record.file_token,
  }));

  return {
    // The rows themselves as well as the payload: the modal re-selects the ones the
    // endpoint could not assign, and the table syncs its checkboxes off row data.
    rows,
    records,
    overBatchLimit: rows.length > MAX_BULK_ASSIGN_RECORDS,
    assignable: rows.length > 0 && records.every((record) => record.parent_type && record.send_token),
  };
};

export const deriveReferralActionState = ({
  selectedData = [],
  flags = {},
  actions = {},
  hasEmrIntegrationCredential = false,
  autoUploadEmr = '',
  allowReplyChartActions = false,
  credentialSupportsStandaloneTask = false,
}) => {
  const { isAReplySelected = () => false, isUploadDisabled = () => true } = actions;
  const { taskCreation, hideTaskButton } = flags;

  const selectedCount = selectedData.length;
  const hasSelection = selectedCount >= 1;
  const selectedAll = selectedData[0] === 'all';
  const singleRecordSelected = selectedCount === 1 && !selectedAll;
  const selectedRecord = singleRecordSelected ? selectedData[0] : null;
  const replySelected = hasSelection && isAReplySelected(selectedData);
  // ENG-2825 (plan D3): each action that HAS a stated capability asks about that capability
  // rather than about "is a reply selected". `replySelected` answers "is this a document?";
  // these answer "may I do X to it?", which is the question a gate actually needs. They were
  // the same question only while `uri` meant both. Keeping them separate is also what lets
  // ENG-3438 turn an action on by flipping the server constant, with no frontend change.
  const downloadDenied = hasSelection && selectionLacksCapability(selectedData, CAPABILITIES.DOWNLOAD);
  const labelDenied = hasSelection && selectionLacksCapability(selectedData, CAPABILITIES.LABEL);
  const notifyDenied = hasSelection && selectionLacksCapability(selectedData, CAPABILITIES.NOTIFY);
  const assignDenied = hasSelection && selectionLacksCapability(selectedData, CAPABILITIES.ASSIGN);
  const sftpDenied = hasSelection && selectionLacksCapability(selectedData, CAPABILITIES.SFTP);
  const viewingArchivedRecords = window.location.search.includes('archived=true');
  const uploadDisabled = selectedRecord
    ? isUploadDisabled(selectedRecord.receiver_type, selectedRecord)
    : true;
  const replyChartActionsAllowed = allowReplyChartActions
    && selectedRecord?.receiver_type === 'reply'
    && !uploadDisabled;
  const chartReplyDisabled = replySelected && !replyChartActionsAllowed;
  const dropdownDisabled = !selectedRecord || chartReplyDisabled;
  const disableChartActions = uploadDisabled || dropdownDisabled;
  const canEdit = !(selectedCount === 1
    && (selectedData[0].can_edit_patient_info || selectedData[0].can_edit_privacy_level));
  const canStandaloneTask = taskCreation && !hideTaskButton && canUseStandaloneTask({
    hasEmrIntegrationCredential,
    credentialSupportsStandaloneTask,
    autoUploadEmr,
  });

  return {
    selectedCount,
    hasSelection,
    selectedAll,
    singleRecordSelected,
    selectedRecord,
    replySelected,
    downloadDenied,
    labelDenied,
    notifyDenied,
    assignDenied,
    sftpDenied,
    replyChartActionsAllowed,
    viewingArchivedRecords,
    uploadDisabled,
    chartReplyDisabled,
    dropdownDisabled,
    disableChartActions,
    canEdit,
    canStandaloneTask,
  };
};

export default deriveReferralActionState;
